import { useState } from "react";
import type { Trade } from "../../api/types";
import { formatDuration, formatMoney, formatPnlMoney, formatTime, formatTradeId } from "../../utils/format";
import { copyToClipboard } from "../../utils/clipboard";

interface TradeRowProps {
  trade: Trade;
  selected: boolean;
  onSelect: (tradeId: string) => void;
}

// Сделка needed more room — bot label + direction badge + short trade id
// (e.g. "d01 LONG #01·1300") was truncating to "#01·…" at the old 120px
// (badges alone ate ~85px of it, reported live). Цена had the most slack
// to give up ("$63 555 → $63 691" never needed anywhere near 208px) with
// Открытие giving up a little too — same total (626px) as before, so the
// table's own minWidth below doesn't need to change. Exported so Trades.tsx
// (the header row) uses this exact same literal instead of a second
// hand-synced copy that could silently drift out of alignment with these
// rows.
export const GRID_COLS = "195px 175px 148px 108px 1fr";

// One row of the Trades table — matches the prototype's grid-based row
// (Сделка / Открытие / Цена / P&L columns) including the accent left-inset
// shadow + soft background used to mark the selected row.
export function TradeRow({ trade, selected, onSelect }: TradeRowProps) {
  const [copied, setCopied] = useState(false);
  const dirColor = trade.direction === "LONG" ? "var(--long)" : "var(--short)";
  const dirSoft = trade.direction === "LONG" ? "var(--longsoft)" : "var(--shortsoft)";
  const pnl = parseFloat(trade.net_pnl);
  const pnlColor = pnl > 0 ? "var(--long)" : pnl < 0 ? "var(--short)" : "var(--fg3)";
  const whenLine = `${formatTime(trade.opened_at, true)} · ${formatDuration(trade.opened_at, trade.closed_at ?? undefined)}`;

  return (
    <button
      type="button"
      onClick={() => onSelect(trade.trade_id)}
      style={{
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        gap: 12,
        alignItems: "center",
        width: "100%",
        padding: "var(--rowpad) 16px",
        border: 0,
        borderBottom: "1px solid var(--line)",
        boxShadow: selected ? "inset 2px 0 0 var(--accent)" : "none",
        background: selected ? "var(--accentsoft)" : "transparent",
        color: "var(--fg)",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
        {trade.bot_label && (
          <span
            title={trade.bot_instance_id}
            style={{
              flex: "none",
              font: "700 9px 'JetBrains Mono',monospace",
              padding: "2px 4px",
              borderRadius: 4,
              background: "var(--surface3)",
              color: "var(--fg3)",
            }}
          >
            {trade.bot_label}
          </span>
        )}
        <span
          style={{
            flex: "none",
            font: "700 10.5px 'Golos Text',sans-serif",
            letterSpacing: ".05em",
            padding: "2px 7px",
            borderRadius: 5,
            background: dirSoft,
            color: dirColor,
          }}
        >
          {trade.direction}
        </span>
        <span
          title={copied ? "Скопировано!" : `${trade.trade_id} · клик — копировать`}
          onClick={async (e) => {
            // The whole row is itself a <button onClick={onSelect}> — stop
            // the click reaching that so copying an id doesn't also select
            // the row (two different actions on the same click otherwise).
            e.stopPropagation();
            if (await copyToClipboard(trade.trade_id)) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }
          }}
          style={{
            font: "400 11.5px 'JetBrains Mono',monospace",
            color: copied ? "var(--long)" : "var(--fg2)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: "pointer",
          }}
        >
          {copied ? "Скопировано!" : formatTradeId(trade.trade_id)}
        </span>
      </span>
      <span style={{ font: "400 12.5px 'JetBrains Mono',monospace", color: "var(--fg2)", whiteSpace: "nowrap" }}>{whenLine}</span>
      <span style={{ display: "flex", alignItems: "center", gap: 7, font: "400 12.5px 'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>
        <span style={{ color: "var(--fg2)" }}>{formatMoney(trade.entry_price, 0)}</span>
        <span style={{ color: "var(--fg3)" }}>→</span>
        <span style={{ color: trade.exit_price ? "var(--fg)" : "var(--warn)" }}>
          {trade.exit_price ? formatMoney(trade.exit_price, 0) : "открыта"}
        </span>
      </span>
      <span style={{ font: "600 13px 'JetBrains Mono',monospace", textAlign: "right", color: pnlColor }}>{formatPnlMoney(trade.net_pnl)}</span>
      <span />
    </button>
  );
}
